import { useRef, useState } from 'react'
import { useTranslation } from 'summon-ui/intl'
import { Modal, Button } from 'summon-ui/mantine'
import ConnectToSocial, { SocialType } from '@components/ConnectToSocial'
import TermsLootdrop from '@features/lootdrops/components/TermsLootdropModal'
import ActionEmailModal from '@features/quests/components/ActionEmailModal'
import ActionSCIDModal from '@features/quests/components/ActionSCIDModal'
import ActionSolanaModal from '@features/quests/components/ActionSolanaModal'
import ActionUsernameModal from '@features/quests/components/ActionUsernameModal'
import useCheckTaskCompletion from '@features/quests/hooks/useCheckTaskCompletion'
import { TaskWithQuestId } from '@features/quests/types'
import { TaskActionType } from '@generated/generates'
import { useViewer } from '@hooks'
import { PopupButton } from '@typeform/embed-react'
import ActionButtonCheck from './ActionButtonCheck'
import styles from './actionButton.module.css'

// Hack to show pending on this specific task
const PENDING_STATE_TASK_ID = 'bee34375-eb75-4bab-9de2-942a60e30934'

type Props = {
  task: TaskWithQuestId
  testId: string
  disabled?: boolean
  lootdropId?: string | undefined
  userAcceptedLootdropTerms?: boolean
}

const ActionButton = ({ task, testId, disabled, lootdropId, userAcceptedLootdropTerms }: Props) => {
  const ref = useRef()
  const t = useTranslation()
  const { viewer } = useViewer()
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false)
  const [isUsernameModalOpen, setIsUsernameModalOpen] = useState(false)
  const [isSolanaModalOpen, setIsSolanaModalOpen] = useState(false)
  const [isSCIDModalOpen, setIsSCIDModalOpen] = useState(false)
  const [isLootdropModalOpen, setIsLootdropModalOpen] = useState(false)

  const {
    checkTaskCompletionMutation,
    isCheckTaskCompletionLoading,
    socialConnect,
    setSocialConnect,
    debouncedInvalidateQuests,
    checkStatus,
    isCheckStatusLoading,
    isUpdatingUserSettings
  } = useCheckTaskCompletion({ task })

  const startTask = () => {
    switch (task.actionType) {
      case TaskActionType.DiscordLogin:
        viewer?.discordUsername
          ? checkTaskCompletionMutation({ questId: task.questId as string, taskId: task.id })
          : setSocialConnect(SocialType.Discord)
        break
      case TaskActionType.AddEmail:
        setIsEmailModalOpen(true)
        break
      case TaskActionType.AddUsername:
        setIsUsernameModalOpen(true)
        break
      case TaskActionType.SolanaWallet:
        setIsSolanaModalOpen(true)
        break
      case TaskActionType.Scid:
        setIsSCIDModalOpen(true)
        break
      case TaskActionType.Typeform:
        // @ts-ignore
        ref.current?.open()
        break
      case TaskActionType.TwitterLogin:
        viewer?.twitterUsername
          ? checkTaskCompletionMutation({ questId: task.questId as string, taskId: task.id })
          : setSocialConnect(SocialType.Twitter)
        break
      case TaskActionType.VisitSite:
        window.open(task?.inputData as string, '_blank')
        checkTaskCompletionMutation({ questId: task.questId as string, taskId: task.id })
        break
      default:
        break
    }
  }

  const isPendingTask = task.id === PENDING_STATE_TASK_ID

  if (lootdropId && !userAcceptedLootdropTerms) {
    return (
      <>
        <Button onClick={() => setIsLootdropModalOpen(true)} miw={145} disabled={disabled}>
          {t('Start')}
        </Button>
        <TermsLootdrop
          lootdropId={lootdropId}
          open={isLootdropModalOpen}
          onClose={() => setIsLootdropModalOpen(false)}
        />
      </>
    )
  }

  return (
    <>
      {task.actionType === TaskActionType.AddEmail && (
        <ActionEmailModal opened={isEmailModalOpen} onClose={() => setIsEmailModalOpen(false)} />
      )}

      {task.actionType === TaskActionType.AddUsername && (
        <ActionUsernameModal opened={isUsernameModalOpen} onClose={() => setIsUsernameModalOpen(false)} />
      )}

      {task.actionType === TaskActionType.SolanaWallet && (
        <ActionSolanaModal opened={isSolanaModalOpen} onClose={() => setIsSolanaModalOpen(false)} />
      )}

      {task.actionType === TaskActionType.Scid && (
        <ActionSCIDModal
          opened={isSCIDModalOpen}
          onClose={() => setIsSCIDModalOpen(false)}
          featureId={task?.featureId}
        />
      )}

      {task.actionType === TaskActionType.Typeform && (
        <PopupButton
          // @ts-ignore the library is not exporting well this type but it's in the docs
          ref={ref}
          id={task.inputData as string}
          hidden={{
            user: viewer?.id as string
          }}
          className='min-w-[125px] hidden'
          onClose={() => debouncedInvalidateQuests(3000)}
        >
          {t('Start')}
        </PopupButton>
      )}

      {task.isCompleted ? (
        <>
          <Button
            variant='outline'
            c='green.8'
            className='pointer-events-none'
            classNames={{ root: styles.completedButtonRoot }}
            miw={145}
          >
            {t('Completed')}
          </Button>
        </>
      ) : task.actionType ? (
        <Button
          data-testid={testId}
          onClick={startTask}
          loading={isUpdatingUserSettings || isCheckTaskCompletionLoading}
          miw={145}
          disabled={disabled}
        >
          {t('Start')}
        </Button>
      ) : task.showManualCheckButton ? (
        <ActionButtonCheck task={task} checkStatus={checkStatus} />
      ) : isPendingTask ? (
        <Button disabled miw={125} className='hidden'>
          {t('Pending')}
        </Button>
      ) : (
        <Button variant='outline' onClick={checkStatus} loading={isCheckStatusLoading} miw={125} disabled={disabled}>
          {t('Check Status')}
        </Button>
      )}

      <Modal className='max-w-[400px]' opened={!!socialConnect} onClose={() => setSocialConnect(null)}>
        <ConnectToSocial type={socialConnect as SocialType} close={() => setSocialConnect(null)} />
      </Modal>
    </>
  )
}

export default ActionButton
